/**
 * Scene 2: The Oil Giants
 * "Three States, One-Third of All Allocations"
 */

import { useRef, useEffect, useMemo } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AnimatedCounter, AnimatedPercentage } from '../AnimatedCounter';
import { NigeriaMap, StateSilhouette } from '../NigeriaMap';
import { OilDerrickIcon, OilDropIcon } from '../../icons/StoryIcons';
import type { StateGeoFeature } from '../../../types';
import type { StoryInsights } from '../../../hooks/useStoryData';
import { MAJOR_OIL_STATES } from '../../../utils/statePathGenerator';

gsap.registerPlugin(ScrollTrigger);

interface Scene2Props {
  insights: StoryInsights;
  features: StateGeoFeature[];
  isActive: boolean;
}

export function Scene2OilGiants({ insights, features, isActive }: Scene2Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const statesContainerRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const oilDerrickRef = useRef<SVGSVGElement>(null);
  
  // Get oil state features
  const oilStateFeatures = useMemo(() => {
    return features.filter(f => MAJOR_OIL_STATES.includes(f.properties.admin1Name));
  }, [features]);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const ctx = gsap.context(() => {
      // Only set initial hidden state if not active
      if (!isActive) {
        gsap.set(titleRef.current, { opacity: 0, y: 50 });
        gsap.set(mapContainerRef.current, { opacity: 0, scale: 0.9 });
        gsap.set(statsRef.current, { opacity: 0, x: 50 });
        
        if (statesContainerRef.current) {
          const cards = statesContainerRef.current.querySelectorAll('.state-card');
          gsap.set(cards, { opacity: 0, y: 30, scale: 0.9 });
        }
      }
      
      // Scroll trigger animation
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top center',
          end: 'center center',
          toggleActions: 'play none none reverse',
        },
      });
      
      tl.to(titleRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      })
      .to(mapContainerRef.current, {
        opacity: 1,
        scale: 1,
        duration: 1,
        ease: 'power3.out',
      }, '-=0.4');
      
      if (statesContainerRef.current) {
        const cards = statesContainerRef.current.querySelectorAll('.state-card');
        tl.to(cards, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          stagger: 0.15,
          ease: 'back.out(1.7)',
        }, '-=0.5');
      }
      
      tl.to(statsRef.current, {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: 'power3.out',
      }, '-=0.3');
      
      // Oil derrick pump animation
      if (oilDerrickRef.current) {
        const pumpArm = oilDerrickRef.current.querySelector('.pump-arm');
        if (pumpArm) {
          gsap.to(pumpArm, {
            rotation: 15,
            transformOrigin: '12px 4px',
            duration: 1,
            repeat: -1,
            yoyo: true,
            ease: 'power1.inOut',
          });
        }
      }
    }, containerRef);
    
    return () => ctx.revert();
  }, []);
  
  // Force visibility when scene becomes active (for auto-scroll)
  useEffect(() => {
    if (isActive) {
      gsap.to(titleRef.current, { opacity: 1, y: 0, duration: 0.5 });
      gsap.to(mapContainerRef.current, { opacity: 1, scale: 1, duration: 0.5 });
      gsap.to(statsRef.current, { opacity: 1, x: 0, duration: 0.5 });
      
      if (statesContainerRef.current) {
        const cards = statesContainerRef.current.querySelectorAll('.state-card');
        gsap.to(cards, { opacity: 1, y: 0, scale: 1, duration: 0.5, stagger: 0.05 });
      }
    }
  }, [isActive]);
  
  // Oil drop positions for background
  const oilDrops = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    left: `${10 + i * 12}%`,
    delay: i * 0.3,
    size: 16 + Math.random() * 16,
  }));
  
  return (
    <section
      ref={containerRef}
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-4 py-20"
    >
      {/* Animated oil drops */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {oilDrops.map(({ id, left, delay, size }) => (
          <OilDropIcon
            key={id}
            className="absolute text-amber-500/10 animate-oil-drop"
            style={{ 
              left, 
              top: '-10%',
              animationDelay: `${delay}s`,
              animationDuration: `${4 + Math.random() * 2}s`,
            }}
            size={size}
          />
        ))}
      </div>
      
      {/* Background glow */}
      <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent pointer-events-none" />
      
      {/* Content */}
      <div className="relative z-10 max-w-6xl w-full">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-12 group">
          <div className="flex items-center justify-center gap-4 mb-4">
            <OilDerrickIcon ref={oilDerrickRef} size={48} className="text-amber-500 transition-all duration-500 group-hover:scale-110 group-hover:drop-shadow-[0_0_12px_rgba(245,158,11,0.5)]" />
            <h2 className="text-3xl md:text-5xl font-display font-bold text-white transition-all duration-300 group-hover:tracking-wide">
              The Oil Giants
            </h2>
          </div>
          <p className="text-xl md:text-2xl text-surface-300">
            Three states receive almost{' '}
            <span className="text-amber-400 font-bold transition-all duration-300 hover:text-amber-300 hover:drop-shadow-[0_0_8px_rgba(251,191,36,0.5)] cursor-default">one-third</span> of all allocations
          </p>
        </div>
        
        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-6 md:gap-12 items-center">
          {/* Map */}
          <div ref={mapContainerRef} className="relative flex items-center justify-center">
            <div className="absolute inset-0 bg-amber-500/10 rounded-full blur-3xl" />
            <div className="w-[260px] h-[260px] sm:w-[320px] sm:h-[320px] md:w-[400px] md:h-[400px] relative z-10 flex items-center justify-center">
              <NigeriaMap
                features={features}
                width={400}
                height={400}
                highlightedStates={MAJOR_OIL_STATES}
                highlightColor="#f59e0b"
                defaultColor="#1e293b"
                className="w-full h-full"
              />
            </div>
          </div>
          
          {/* Stats and state cards */}
          <div className="space-y-4 md:space-y-8">
            {/* State cards */}
            <div ref={statesContainerRef} className="grid gap-3 md:gap-4">
              {insights.topOilStates.slice(0, 3).map((state, index) => {
                const feature = oilStateFeatures.find(
                  f => f.properties.admin1Name === state.state
                );
                
                return (
                  <div
                    key={state.state}
                    className="state-card group/card flex items-center gap-2 sm:gap-4 p-3 sm:p-4 bg-surface-800/50 backdrop-blur-sm rounded-xl border border-amber-500/20 cursor-pointer transition-all duration-300 hover:bg-surface-800/70 hover:border-amber-500/40 hover:shadow-lg hover:shadow-amber-500/10 hover:-translate-y-1 hover:scale-[1.02]"
                  >
                    <div className="flex items-center justify-center w-8 h-8 sm:w-12 sm:h-12 rounded-lg bg-amber-500/20 text-amber-400 font-bold text-sm sm:text-lg shrink-0 transition-all duration-300 group-hover/card:bg-amber-500/30 group-hover/card:scale-110">
                      {index + 1}
                    </div>
                    
                    {feature && (
                      <div className="hidden sm:block shrink-0 transition-transform duration-300 group-hover/card:scale-110">
                        <StateSilhouette
                          feature={feature}
                          size={60}
                          color="#f59e0b"
                          glowColor="#f59e0b"
                        />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-lg font-semibold text-white truncate transition-colors duration-300 group-hover/card:text-amber-200">{state.state}</h3>
                      <p className="text-xs sm:text-sm text-surface-400 transition-colors duration-300 group-hover/card:text-surface-300">
                        ₦{(state.total / 1e12).toFixed(2)}T total
                      </p>
                    </div>
                    
                    <div className="text-right shrink-0">
                      <div className="text-lg sm:text-2xl font-bold text-amber-400 transition-all duration-300 group-hover/card:text-amber-300 group-hover/card:scale-110">
                        <AnimatedPercentage target={state.percentage} animate={isActive} />
                      </div>
                      <div className="text-xs text-surface-500 transition-colors duration-300 group-hover/card:text-surface-400">of total</div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Summary stats */}
            <div ref={statsRef} className="grid grid-cols-2 gap-2 sm:gap-4">
              <div className="p-3 sm:p-6 bg-surface-800/50 backdrop-blur-sm rounded-xl border border-surface-700">
                <div className="text-xl sm:text-3xl font-display font-bold text-amber-400 mb-1 sm:mb-2">
                  <AnimatedPercentage 
                    target={insights.oilStatesPercentage} 
                    animate={isActive} 
                  />
                </div>
                <div className="text-xs sm:text-sm text-surface-400">
                  Share of all 9 oil states
                </div>
              </div>
              
              <div className="p-3 sm:p-6 bg-surface-800/50 backdrop-blur-sm rounded-xl border border-surface-700">
                <div className="text-xl sm:text-3xl font-display font-bold text-white mb-1 sm:mb-2">
                  ₦<AnimatedCounter
                    target={insights.oilStatesTotal / 1e12}
                    duration={2}
                    decimals={2}
                    animate={isActive}
                  />T
                </div>
                <div className="text-xs sm:text-sm text-surface-400">
                  Total to oil states
                </div>
              </div>
            </div>
            
            {/* Insight text */}
            <p className="text-sm sm:text-base text-surface-300 text-center lg:text-left">
              Due to the <span className="text-amber-400 font-medium">13% derivation principle</span>,
              oil-producing states receive additional allocations based on their contribution to 
              Nigeria's petroleum revenue.
            </p>
          </div>
        </div>
      </div>
      
      {/* CSS for oil drop animation */}
      <style>{`
        @keyframes oil-drop {
          0% {
            transform: translateY(-100%) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 0.3;
          }
          90% {
            opacity: 0.3;
          }
          100% {
            transform: translateY(120vh) rotate(360deg);
            opacity: 0;
          }
        }
        .animate-oil-drop {
          animation: oil-drop 4s ease-in infinite;
        }
      `}</style>
    </section>
  );
}

export default Scene2OilGiants;

